import dayjs from 'dayjs'

import { ref, h, render, getCurrentInstance, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { jsPDF } from 'jspdf'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { getI18n } from '@/plugins/i18n'

import TextBox from 'devextreme/ui/text_box'
import SelectBox from 'devextreme/ui/select_box'
import DropDownBox from 'devextreme/ui/drop_down_box'
import TagBox from 'devextreme/ui/tag_box'
import DateBox from 'devextreme/ui/date_box'
import NumberBox from 'devextreme/ui/number_box'
import CustomStore from 'devextreme/data/custom_store'
import DataSource from 'devextreme/data/data_source'
import ODataStore from 'devextreme/data/odata/store'
import CheckBox from 'devextreme/ui/check_box'
import { exportDataGrid as pdfExporter } from 'devextreme/pdf_exporter'
import { exportDataGrid as excelExporter } from 'devextreme/excel_exporter'

import { useWebSiteStore, elementLayoutTypeEnum } from '@/stores/web-site.store'

import { getCurrencyPrecision, useCurrency } from '@/composables/currency'

import { useException } from '@/composables/exceptions'
import { HIGHLIGHT_DURATION_MS } from '@/composables/scroll'

import GridRefreshButton from '@/components/shared/GridRefreshButton.vue'
import ElementLayoutSelector from '@/components/shared/ElementLayoutSelector.vue'

const altPressed = ref(false)

const enterPressedTimestamp = ref(0)
const escapePressedTimestamp = ref(0)

export const EXPORT_FORMAT = Object.freeze({ XLSX: 'XLSX', PDF: 'PDF' })

export const focusedRow = ref(null)
export const masterGridFocusedRow = ref(null)
export const focusedGrid = ref(null)
export const defaultGridHeight = '90vh'

export const editingModeEnum = {
  NONE: 0,
  INSERT: 1,
  UPDATE: 2,
  DELETE: 3,
}

export const editingMode = ref(0)

export const useGridRef = () => {
  const gridRef = ref(null)
  const gridIsEditing = () => editingMode.value === editingModeEnum.INSERT || editingMode.value === editingModeEnum.UPDATE
  return { gridRef, focusedRow, editingMode, editingModeEnum, gridIsEditing }
}

// Applies the `?<param>=...` route-query values as column filters: on load, and again when the
// query changes while the page is open (e.g. an AI chat link to another row of the same page).
// `columns`: a column name, a list of them, or a { queryParam: column } map.
export const useRouteColumnFilter = (gridRef, columns = 'code') => {
  const route = useRoute()
  const router = useRouter()
  const columnsByParam = Array.isArray(columns) || typeof columns === 'string'
    ? Object.fromEntries([columns].flat().map(column => [column, column]))
    : columns
  const applyRouteFilter = () => {
    const instance = gridRef.value?.instance
    const appliedParams = Object.keys(columnsByParam).filter(param => route.query[param] != null)
    if (!appliedParams.length || !instance) return
    appliedParams.forEach(param => {
      const column = columnsByParam[param]
      const values = [route.query[param]].flat().map(value => (instance.columnOption(column, 'dataType') === 'number' ? Number(value) : value))
      if (values.length > 1) instance.columnOption(column, 'filterValues', values)
      else instance.columnOption(column, 'filterValue', values[0])
    })
    // Drop the query params once applied so the filter isn't re-applied on later
    // grid reloads (e.g. after the user clears filters with the toolbar button).
    const query = { ...route.query }
    appliedParams.forEach(param => delete query[param])
    router.replace({ query })
  }
  watch(() => Object.keys(columnsByParam).map(param => route.query[param]), applyRouteFilter)
  return { onContentReady: applyRouteFilter }
}

const refreshAndHighlightRowAsync = async(grid, rowId = null) => {
  await grid.refresh()
  if (rowId == null) return
  clearTimeout(grid.highlightTimer)
  await grid.navigateToRow(rowId)
  grid.option('focusedRowKey', rowId)
  grid.highlightedRowKey = rowId
  grid.repaint()
  grid.highlightTimer = setTimeout(() => {
    grid.highlightedRowKey = null
    grid.repaint()
  }, HIGHLIGHT_DURATION_MS)
}

export const refreshGridAsync = async(gridRef, createdRowId = null) => {
  const grid = gridRef.value?.instance
  if (grid) await refreshAndHighlightRowAsync(grid, createdRowId)
}

export const getCreatedRowId = (editItem, saved) => (editItem && !editItem.id ? saved?.id ?? null : null)

export const refreshChangedRowAsync = async(gridRef, change, getByIdAsync = null) => {
  if (getByIdAsync && change.id && !change.created && gridRef.value?.instance?.getRowIndexByKey(change.id) >= 0) {
    await useUpdateMasterDetail().repaintRow(gridRef, getByIdAsync, change.id, change.id)
    return
  }
  await refreshGridAsync(gridRef, change.created ? change.id : null)
}

const onDefaultRowPrepared = (e) => {
  if (e.rowType === 'data' && e.key != null && e.key === e.component.highlightedRowKey) {
    e.rowElement.classList.add('row-highlight')
  }
}

export const useCurrencyColumnFormatter = (rowData, field, currencyId) => {
  const webSiteStore = useWebSiteStore()
  const { getCurrencyString } = useCurrency()
  const defaultCurrencyCode = webSiteStore.getDefaultCurrency()?.code
  const currentCurrency = webSiteStore.currencies.find(i => i.id === currencyId)
  return rowData[field] !== undefined && getCurrencyString(+rowData[field], currentCurrency?.code || defaultCurrencyCode)
}

export const usePercentageOptions = (options = null) => {
  const opts = {...{ precision: 2, isEditor: false }, ...options}
  const format = { type: 'percent', precision: opts.precision }
  return { format: format, step: 0.01 }
}

export const useCurrencyOptions = (options = null) => {
  const webSiteStore = useWebSiteStore()
  const currencyCode = options?.currency || webSiteStore.getDefaultCurrency()?.code
  const format = { type: 'currency', currency: currencyCode, precision: getCurrencyPrecision(), useGrouping: true }
  return { format: format }
}

export const useDateConverter = (model, excludedFields = []) => {
  for (const prop in model) {
    if (!excludedFields.includes(prop) && model[prop] instanceof Date) {
      model[prop] = dayjs(model[prop]).format('YYYY-MM-DD')
    }
  }
  return model
}

export const useLookup = (options = null) => {
  const defaultOptions = {
    valueExpr: 'id',
    sort: 'code',
    displayExpr: (obj) => !obj ? '' : `${obj.code} - ${obj.name}`
  }

  const opts = {...defaultOptions, ...options }
  return {
    dataSource: opts.dataSource || { store: opts.store, sort: opts.sort },
    ...opts
  }
}

export const useDefaultPriceRounding = (e, toPrecisionFields, precision = 4) => {
  for (const field of toPrecisionFields) {
    if (e.newData[field]) {
      e.newData[field] = +e.newData[field].toPrecision(4)
    }
  }
}

export const useDefaultOnRowInserting = (e) => useDateConverter(e.data)
export const useDefaultOnRowUpdating = (e) => useDateConverter(e.newData)

export const useDefaultOnInitNewRow = (e) => {
  e.component.keyboardListenerEnabled = false
  e.component.clearSelection()
  editingMode.value = editingModeEnum.INSERT
  const editingPopupTitleFn = e.component.option('editing.popup.titleFn')
  if (editingPopupTitleFn) {
    e.component.option('editing.popup.title', editingPopupTitleFn(e.data))
  }
}

export const useDefaultOnEditingStart = (e) => {
  e.component.keyboardListenerEnabled = false
  e.component.clearSelection()
  editingMode.value = editingModeEnum.UPDATE

  const editingPopupTitleFn = e.component.option('editing.popup.titleFn')
  if (editingPopupTitleFn) {
    e.component.option('editing.popup.title', editingPopupTitleFn(e.data))
  }
}

export const useDefaultOnEditCanceled = (e) => {
  e.component.keyboardListenerEnabled = true
  editingMode.value = editingModeEnum.NONE
  useFocusOnDatagrid(e)
}

export const useDefaultPager = {
  visible: false,
}

export const useDefaultPaging = {
  pageSize: 20,
  enabled: true,
}

export const useDefaultScrolling = {
  mode: 'virtual',
}

export const useDefaultFilterRow = {
  visible: true,
  applyFilter: 'auto'
}

export const useDefaultSorting = {
  mode: 'multiple'
}

export const useDefaultGrouping = {
  autoExpandAll: false,
}

export const useDefaultGroupPanel = {
  visible: true,
}

export const useDefaultHeaderFilter = {
  visible: true,
  allowSearch: true,
}

export const useDefaultColumnChooser = {
  enabled: true,
  mode: 'select',
  position: { my: 'right top', at: 'right bottom', of: '.dx-datagrid-column-chooser-button' },
}

export const useDefaultExport = (options) => {
  options = {
    fileName: 'export',
    isLandscape: true,
    formats: [EXPORT_FORMAT.XLSX],
    ...options
  }

  const exportOptions = {
    enabled: true,
    allowExportSelectedData: true,
    formats: options.formats,
  }

  const exportToPdf = async(e) => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF(options.isLandscape ? 'l' : 'p', 'mm', [210, 297]) // portrait A4

    await pdfExporter({
      jsPDFDocument: doc,
      component: e.component,
      selectedRowsOnly: e.selectedRowsOnly,
      indent: 5,
      customizeCell: options.customizeCellPdf,
    })

    doc.save(`${options.fileName}.pdf`)
  }

  const exportToExcel = async(e) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet(options.fileName)

    await excelExporter({
      component: e.component,
      selectedRowsOnly: e.selectedRowsOnly,
      worksheet,
      customizeCell: options.customizeCellExcel,
    })
    const buffer = await workbook.xlsx.writeBuffer()
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${options.fileName}.${e.format.toLowerCase()}`)
  }

  const onExporting = async(e) => {
    e.cancel = true
    if (e.format === EXPORT_FORMAT.PDF) {
      await exportToPdf(e)
    } else {
      await exportToExcel(e)
    }
  }

  return { exportOptions, onExporting, exportToExcel, exportToPdf }
}

export const useDefaultRowReordering = (reorderingApi, itemId, gridRef) => {
  const onDragEnd = async(e) => {
    if (e.fromIndex === e.toIndex) {
      return
    }
    const fromItem = gridRef.value.instance.getDataSource().items()[e.fromIndex]
    const toItem = gridRef.value.instance.getDataSource().items()[e.toIndex]
    await reorderingApi(itemId, {
      fromId: fromItem?.id || fromItem?.key,
      toId: toItem?.id || toItem?.key,
      isDropInsideItem: e.dropInsideItem
    })
    gridRef.value.instance.refresh()
  }

  return { onDragEnd }
}

export const useDefaultErrorHandler = async(e) => {
  const { t } = getI18n()

  const setValidationOnEditor = (editor, detail, label) => {
    editor.option({
      validationStatus: 'invalid',
      validationErrors: detail.errors.map(i => {
        return { message: t(i.code, {field: label, ...i.params}) || i.code || i }
      })
    })
  }

  let isManaged = false
  if (e.error?.httpStatus >= 400 && e.error?.httpStatus < 500) {
    if (e.error?.httpStatus === 401 && !e.component._isRetrying) {
      e.component._isRetrying = true
      const webSiteStore = useWebSiteStore()
      try {
        await webSiteStore.refreshTokenAsync({ _isRetrying: true })
        await e.component.refresh()
        return
      } catch (err) {
        await webSiteStore.logoutAsync()
        return Promise.reject(err)
      }
    }

    if (e?.error?.errorDetails?.details && e?.error?.errorDetails.details.length > 0) {
      e.error.errorDetails.details.forEach(detail => {
        const searchRowIndex = editingMode.value === editingModeEnum.INSERT ? 0 : focusedRow.value.rowIndex

        // The get cell element uses the rowIndex and the column index to get the cell element from the GRID or from the "edit" POPUP
        // If you do not specify the current editing row index, the cell element will be retrieved from the GRID
        const cellElement = e.component.getCellElement(searchRowIndex, detail.target)
        if (cellElement) {
          let editorElements = cellElement.getElementsByClassName('dx-texteditor')
          let editorInstance = TextBox.getInstance(editorElements[0])
          if (!editorInstance) {
            editorInstance = SelectBox.getInstance(editorElements[0])
          }
          if (!editorInstance) {
            editorInstance = DropDownBox.getInstance(editorElements[0])
          }
          if (!editorInstance) {
            editorInstance = TagBox.getInstance(editorElements[0])
          }
          if (!editorInstance) {
            editorInstance = DateBox.getInstance(editorElements[0])
          }
          if (!editorInstance) {
            editorInstance = NumberBox.getInstance(editorElements[0])
          }
          if (!editorInstance) {
            editorElements = cellElement.getElementsByClassName('dx-checkbox')

            for (const item of editorElements) {
              const inputs = item.getElementsByTagName('input')
              if (inputs.length && inputs[0].value === 'null') {
                editorInstance = CheckBox.getInstance(item)
                break
              }
            }
          }
          if (editorInstance) {
            isManaged = true
            const label = editorInstance.option('label')
            setValidationOnEditor(editorInstance, detail, label)
          }
        }
      })
    }
  }
  if (!isManaged) {
    console.error(e.error?.errorDetails || e.error)
    const { processError } = useException()
    processError(e.error)
  }
}

export const useDefaultEditing = (title, form, options = null) => {
  const { t } = getI18n()

  const defaultOptions = {
    width: '70%', height: 'auto',
    allowAdding: true,
    allowUpdating: true,
    allowDeleting: true,
  }
  const opts = {...defaultOptions, ...options}

  return {
    allowAdding: opts.allowAdding,
    allowUpdating: opts.allowUpdating,
    allowDeleting: opts.allowDeleting,
    useIcons: true,
    mode: 'popup',
    popup: {
      title: title || t('common.edit'),
      titleFn: typeof title === 'function' ? title : null,
      showTitle: true,
      width: opts.width || 'auto',
      height: opts.height || 'auto',
    },
    form: form || {},
  }
}

export const stripGridStateVolatile = (state) => {
  if (!state) {
    return state
  }
  state.columns?.forEach((col) => {
    if (col.filterValue) {
      col.filterValue = null
    }
  })
  state.selectedRowKeys = null
  return state
}

export const useDefaultStateStoring = (gridId) => {
  const webSiteStore = useWebSiteStore()
  const gridLoadState = (gridId) => stripGridStateVolatile(webSiteStore.gridStates[gridId])

  const gridSaveState = (gridId, state) => {
    webSiteStore.setGridState(gridId, state)
  }

  return {
    enabled: true,
    type: 'custom',
    customLoad: () => gridLoadState(gridId),
    customSave: (state) => gridSaveState(gridId, state)
  }
}

export const masterGrid = ref(null)
export const detailGrid = ref(null)

export const gridAllowEditingAction = (grid, action) => {
  if (grid.option('editing')[action] instanceof Function) {
    return grid.option('editing')[action]({row: focusedRow.value})
  }
  return grid.option('editing')[action]
}

export const useDefaultKeyDownListener = () => {
  return (event) => {
    if (event.code === 'AltLeft' || event.code === 'AltRight') {
      altPressed.value = true
    } else if (altPressed.value && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
    } else if (event.code === 'F3' && !editingMode.value && focusedGrid.value?.keyboardListenerEnabled && gridAllowEditingAction(focusedGrid.value, 'allowAdding')) {
      focusedGrid.value.addRow()
      event.preventDefault()
    } else if (event.code === 'F4' && !editingMode.value && focusedGrid.value?.keyboardListenerEnabled && gridAllowEditingAction(focusedGrid.value, 'allowUpdating')) {
      focusedGrid.value.editRow(focusedRow.value.rowIndex)
      event.preventDefault()
    } else if (event.code === 'F6' && !editingMode.value && focusedGrid.value?.keyboardListenerEnabled && gridAllowEditingAction(focusedGrid.value, 'allowDeleting')) {
      focusedGrid.value.deleteRow(focusedRow.value.rowIndex)
      editingMode.value = editingModeEnum.DELETE
      event.preventDefault()
    }
  }
}

export const useDefaultKeyUpListener = () => {
  return (event) => {
    if (event.code === 'AltLeft' || event.code === 'AltRight') {
      altPressed.value = false
    } else if (editingMode.value && editingMode.value !== editingModeEnum.DELETE && event.key === 'Enter') {
      if ((Date.now() - enterPressedTimestamp.value) < 500) {
        document.activeElement.blur()
        focusedGrid.value.getController('validating').validate(true).done(isValid => isValid && focusedGrid.value.saveEditData())
      } else {
        enterPressedTimestamp.value = Date.now()
      }
      event.preventDefault()
      event.stopPropagation()
    } else if (editingMode.value === editingModeEnum.DELETE && event.key === 'Escape') {
      focusedGrid.value.cancelEditData()
      event.preventDefault()
      event.stopPropagation()
    } else if (editingMode.value && event.key === 'Escape') {
      if ((Date.now() - escapePressedTimestamp.value) < 500) {
        focusedGrid.value.cancelEditData()
        focusedGrid.value.refresh()
      } else {
        escapePressedTimestamp.value = Date.now()
      }
      event.preventDefault()
      event.stopPropagation()
    }
    if (altPressed.value) {
      altPressed.value = false
      if (event.key === 'ArrowDown' && !editingMode.value && masterGridFocusedRow.value) {
        masterGrid.value.expandRow(masterGridFocusedRow.value.key)
        masterGrid.value.keyboardListenerEnabled = false // to avoid the default behavior of the grid while opening the detail
        event.preventDefault()
      } else if (event.key === 'ArrowUp' && !editingMode.value && masterGridFocusedRow.value) {
        if (masterGrid.value.isRowExpanded(masterGridFocusedRow.value.key)) {
          masterGrid.value.collapseRow(masterGridFocusedRow.value.key)
          masterGrid.value.refresh()
        }
        event.preventDefault()
      }
    }
  }
}

export const useDefaultOnFocusedRowChanging = (e) => {
  if (altPressed.value) {
    e.cancel = true
  }
  if (!e.component.keyboardListenerEnabled) {
    e.cancel = true
  }
}

export const useDefaultOnRowExpandingDatagrid = (e) => {
  if (e.component.option('masterDetail').enabled && !e.component.customOptions?.allowMultipleMasterDetail) {
    e.component.collapseAll(-1)
  }
}

export const useDefaultOnFocusedRowChanged = (e) => {
  focusedRow.value = e.row
  if (e.component.option('masterDetail').enabled) {
    masterGrid.value = e.component
    masterGridFocusedRow.value = e.row
  }
  e.component.keyboardListenerEnabled = true
}

// The record the chat talks about: the row whose detail is open, else the only selected row; a focused row does not count.
const syncGridEntity = (grid) => {
  if (grid.element().closest('.dx-master-detail-cell')) return
  const selected = grid.getSelectedRowsData()
  useWebSiteStore().setGridEntity(grid.expandedRowData ?? (selected.length === 1 ? selected[0] : null))
}

const onGridRowExpanded = (e) => {
  e.component.expandedRowData = e.component.getVisibleRows().find(row => row.key === e.key)?.data ?? null
  syncGridEntity(e.component)
}

const onGridRowCollapsed = (e) => {
  if (e.component.expandedRowData && e.component.keyOf(e.component.expandedRowData) === e.key) e.component.expandedRowData = null
  syncGridEntity(e.component)
}

export const useDefaultKeyHandler = () => {
  const keyDownListener = useDefaultKeyDownListener()
  const keyUpListener = useDefaultKeyUpListener()
  document.addEventListener('keydown', keyDownListener)
  document.addEventListener('keyup', keyUpListener)
}

export const useDefaultOnContentReady = (e) => {
  const grid = e.component
  if (grid) {
    if (!grid.firstLoad) {
      grid.firstLoad = true
      setTimeout(() => {
        useFocusOnDatagrid(e)
      }, 50)
    }

    grid.columnOption('command:edit', 'visibleIndex', -1)
    grid.columnOption('command:edit', 'fixed', 'true')
    grid.columnOption('command:edit', 'fixed-position', 'left')
  }
}

// DevExtreme headers only follow the rows' scroll, so a touch swipe on them would do nothing.
export const bindHeaderTouchScroll = (grid) => {
  const headers = grid.element().querySelector('.dx-datagrid-headers')
  if (!headers || headers.dataset.touchScrollBound) return
  headers.dataset.touchScrollBound = 'true'
  let lastX = null
  headers.addEventListener('touchstart', (event) => { lastX = event.touches[0].clientX }, { passive: true })
  headers.addEventListener('touchmove', (event) => {
    const x = event.touches[0].clientX
    grid.getScrollable()?.scrollBy({ left: lastX - x })
    lastX = x
  }, { passive: true })
}

export const useFocusOnDatagrid = (e) => {
  const grid = e.component
  focusedGrid.value = grid
  if (grid.option('masterDetail')?.enabled) {
    masterGrid.value = grid
  }
  return setFocusOnDatagrid(grid)
}

const setFocusOnDatagrid = (grid) => {
  if (grid) {
    grid.focus()
    grid.keyboardListenerEnabled = true
  }
}

export const useDefaultAddressEditing = (addressType, colSpan, colCount) => {
  const { t } = getI18n()
  return {
    itemType: 'group',
    caption: t('common.address'),
    colSpan: colSpan,
    colCount: colCount,
    items: [
        { dataField: `${addressType}.country`, label: { text: t('common.country') } },
        { dataField: `${addressType}.state`, label: { text: t('common.state') }, editorOptions: { showClearButton: true } },
        { dataField: `${addressType}.city`, label: { text: t('common.city') }},
        { dataField: `${addressType}.zip_code`, label: { text: t('common.zipCode') } },
        { dataField: `${addressType}.street`, label: { text: t('common.street') }, colSpan: 2 },
    ]
  }
}

export const onDefaultSaved = (e) => {
  editingMode.value = editingModeEnum.NONE
  useFocusOnDatagrid(e)
}

export const onDefaultRowRemoved = (e) => {
  editingMode.value = editingModeEnum.NONE
  useFocusOnDatagrid(e)
}

export const useDefaultOnOptionChanged = (e) => {
  if (e.name === 'columns' && e.fullName?.endsWith('.visible')) {
    e.component.updateDimensions()
  }
  if (e.component.customElements?.clearFilterButton) {
    const grid = e.component
    const clearFilterButton = grid.customElements.clearFilterButton
    const hasFilter = grid.getCombinedFilter() && grid.getCombinedFilter().length > 0
    if (clearFilterButton?.options.disabled === !hasFilter) return
    clearFilterButton.options.disabled = !hasFilter
    grid.repaint()
  }
}

export const renderMenuItem = (data, _index, element) => {
  const iconContainer = document.createElement('div')
  iconContainer.className = 'dx-list-item-icon-container'
  const icon = document.createElement('i')
  icon.className = `dx-icon dx-list-item-icon ${data.icon}`
  iconContainer.append(icon)
  element.title = data.hint
  element.append(iconContainer, data.text)
}

const useExportMenu = (grid, options) => {
  const { t } = getI18n()
  const presentationExports = options?.presentationExports ?? []
  const gridDataEnabled = options?.export?.enabled !== false
  if (!presentationExports.length && !gridDataEnabled) return []

  const canSelectRows = ['single', 'multiple'].includes(options?.selection?.mode)
  const exportGridData = (selectedRowsOnly) => options.onExporting?.({ component: grid, format: 'XLSX', selectedRowsOnly, cancel: false })

  const buildItems = (selectedRowsData) => [
    ...presentationExports.map(presentationExport => ({
      text: t('common.presentationExport', { name: presentationExport.label }),
      hint: t('common.presentationExportHint'),
      icon: 'mdi mdi-file-document-outline',
      disabled: presentationExport.isDisabled?.(selectedRowsData) ?? false,
      onClick: () => presentationExport.onClick(selectedRowsData),
    })),
    ...(gridDataEnabled ? [{
      text: t('common.gridDataAllRows'),
      hint: t('common.gridDataExportHint'),
      icon: 'mdi mdi-table',
      onClick: () => exportGridData(false),
    }] : []),
    ...(gridDataEnabled && canSelectRows ? [{
      text: t('common.gridDataSelectedRows'),
      hint: t('common.gridDataExportHint'),
      icon: 'mdi mdi-table-check',
      disabled: !selectedRowsData.length,
      onClick: () => exportGridData(true),
    }] : []),
  ]

  let menu = null
  grid.on('selectionChanged', (e) => menu?.option('items', buildItems(e.selectedRowsData)))

  return [{
    widget: 'dxDropDownButton',
    location: 'after',
    options: {
      icon: 'export',
      text: t('common.export'),
      useSelectMode: false,
      items: buildItems([]),
      itemTemplate: renderMenuItem,
      dropDownOptions: { width: 'auto', minWidth: '16rem' },
      onItemClick: (e) => e.itemData.onClick(),
      onInitialized: (e) => { menu = e.component },
    },
  }]
}

// DevExtreme only re-lays out on window resize: a container that narrows (e.g. the docked chat panel) needs this.
const observeGridWidth = (grid) => {
  let width = null
  const observer = new ResizeObserver(([entry]) => {
    if (entry.contentRect.width === width) return
    const isFirstMeasure = width === null
    width = entry.contentRect.width
    if (!isFirstMeasure) requestAnimationFrame(() => grid.updateDimensions())
  })
  observer.observe(grid.element())
  grid.on('disposing', () => observer.disconnect())
}

export const useDefaultOnInitialized = (e, options = null) => {
  const { t } = getI18n()

  const grid = e.component
  grid.customElements = {}
  grid.customOptions = options
  grid.on('initNewRow', (e) => useDefaultOnInitNewRow(e))
  grid.on('editingStart', (e) => useDefaultOnEditingStart(e))
  grid.on('rowInserting', (e) => useDefaultOnRowInserting(e))
  grid.on('rowUpdating', (e) => useDefaultOnRowUpdating(e))
  grid.on('rowRemoved', (e) => onDefaultRowRemoved(e))
  grid.on('editCanceled', (e) => useDefaultOnEditCanceled(e))
  grid.on('contentReady', (e) => useDefaultOnContentReady(e))
  grid.on('focusedRowChanged', (e) => useDefaultOnFocusedRowChanged(e))
  grid.on('focusedRowChanging', (e) => useDefaultOnFocusedRowChanging(e))
  grid.on('rowCollapsed', (e) => useFocusOnDatagrid(e))
  grid.on('rowCollapsed', onGridRowCollapsed)
  grid.on('rowExpanded', onGridRowExpanded)
  grid.on('selectionChanged', (e) => syncGridEntity(e.component))
  grid.on('disposing', (e) => { if (!e.element.closest('.dx-master-detail-cell')) useWebSiteStore().setGridEntity(null) })
  observeGridWidth(grid)
  grid.on('rowExpanding', (e) => useDefaultOnRowExpandingDatagrid(e))
  grid.on('optionChanged', (e) => useDefaultOnOptionChanged(e))
  grid.on('saved', (e) => onDefaultSaved(e))
  grid.on('rowPrepared', (e) => onDefaultRowPrepared(e))
  grid.on('rowInserted', (e) => refreshAndHighlightRowAsync(e.component, e.key))
  grid.on('keyDown', (e) => {
    if (e.event.key === 'ArrowLeft' || e.event.key === 'ArrowRight' || e.event.key === 'Tab') {
      e.handled = true
      e.event.preventDefault()
    }
  })

  const layoutSelectorId = options?.saveStateId || options?.id
  const webSiteStore = useWebSiteStore()
  const layoutSelectorItem = layoutSelectorId
    ? [{
      location: 'after',
      template: (_data, _index, container) => {
        const vnode = h(ElementLayoutSelector, {
          elementType: elementLayoutTypeEnum.GRID,
          elementKey: layoutSelectorId,
          icon: 'mdi-view-grid-plus-outline',
          menuTitle: t('common.layout'),
          supportsDefault: true,
          builtins: [{ label: t('common.defaultLayout'), value: null, icon: 'mdi-view-grid-outline' }],
          captureData: () => stripGridStateVolatile(grid.state()),
          onLoad: (layout) => grid.state(JSON.parse(JSON.stringify(layout.data))),
          onSelectBuiltin: () => {
            webSiteStore.setGridState(layoutSelectorId, null)
            grid.state({})
          },
          onDefaultResolved: ({ layout }) => {
            if (layout && !webSiteStore.gridStates?.[layoutSelectorId]) {
              grid.state(JSON.parse(JSON.stringify(layout.data)))
            }
          },
        })
        vnode.appContext = options?.appContext
        render(vnode, container)
        grid.on('disposing', () => render(null, container))
      },
    }]
    : []

  const verticalSeparator = () => ({
    location: 'after',
    template: (_data, _index, container) => {
      const separator = document.createElement('div')
      separator.className = 'toolbar-separator'
      container.append(separator)
    },
  })

  const importWidget = options?.onImport
    ? [{
      widget: 'dxButton',
      location: 'after',
      options: { icon: 'import', hint: t('common.import'), onClick: options.onImport },
    }]
    : []

  const exportGroup = [
    ...(options?.exportWidgets ?? []),
    ...useExportMenu(grid, options),
    ...(options?.dataActionWidgets ?? []),
    ...importWidget,
  ]

  const gridSpecificGroup = [
    ...(options?.addToolbarItems ?? []),
    ...(options?.extraToolbarItems ?? []),
  ]

  const viewGroup = [
    ...(options?.viewToolbarItems ?? []),
    ...(options?.archivedToggle ? [{ location: 'after', template: 'archived-toggle' }] : []),
    ...layoutSelectorItem,
    { name: 'columnChooserButton', locateInMenu: 'never' },
  ]

  const toAddActionItem = (btn) => ({
    widget: 'dxButton',
    location: 'after',
    visible: btn.visible ?? options.allowAdding !== false,
    options: { icon: btn.icon?.startsWith('mdi-') ? `mdi ${btn.icon}` : btn.icon, text: btn.text ?? btn.label, hint: btn.hint ?? btn.label, disabled: false, onClick: btn.onClick },
  })
  const plainAddItems = options.addButton
    ? [toAddActionItem({ icon: 'mdi-plus', hint: t('common.add'), ...options.addButton })]
    : options.addButtons ? [] : [{ name: 'addRowButton', locateInMenu: 'never' }]
  const addActionItems = [...(options.addButtons ?? []).map(toAddActionItem), ...plainAddItems]

  if (options?.clearFilterButton) {
    grid.customElements.clearFilterButton = {
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'mdi mdi-filter-off',
        hint: t('common.clearFilter'),
        disabled: !(grid.instance().getCombinedFilter() && grid.instance().getCombinedFilter().length > 0),
        onClick: (e) => {
          if (options.onClearFilter) {
            options.onClearFilter({ component: grid })
          }
          grid.instance().clearFilter()
        },
      }
    }
  }

  const refreshItem = options?.hideRefreshButton
    ? []
    : [{
      location: 'after',
      template: (_data, _index, container) => {
        const vnode = h(GridRefreshButton, { action: () => grid.refresh() })
        vnode.appContext = options?.appContext
        render(vnode, container)
        grid.on('disposing', () => render(null, container))
      },
    }]

  const actionGroup = [
    ...addActionItems,
    ...(grid.customElements.clearFilterButton ? [grid.customElements.clearFilterButton] : []),
    ...refreshItem,
  ]

  const groups = [exportGroup, gridSpecificGroup, viewGroup, actionGroup].filter(g => g.length)
  const afterItems = groups.flatMap((group, index) => index < groups.length - 1 ? [...group, verticalSeparator()] : group)

  const toolbarItems = [
    ...(options.groupPanel?.visible ? [{ name: 'groupPanel', location: 'before' }] : []),
    ...(options?.beforeToolbarItems ?? []),
    ...afterItems,
  ]

  grid.option('toolbar', { items: toolbarItems })
}

export const useDefaultGridOptions = (options = null) => {
  const appContext = getCurrentInstance()?.appContext
  const defaultExport = options?.isDetailGrid ? null : useDefaultExport({ fileName: options?.exportFileName || 'export' })
  const currentOptions = {...{
    // columns
    // https://js.devexpress.com/Demos/WidgetsGallery/Demo/DataGrid/ColumnCustomization/Vue/Light/
    allowColumnReordering: true,
    allowColumnResizing: true,
    showColumnLines: true,
    columnChooser: useDefaultColumnChooser,
    columnResizingMode: 'widget',
    showBorders: true,
    remoteOperations: { filtering: true, sorting: true, paging: true, grouping: false },
    columnAutoWidth: false,
    syncLookupFilterValues: false,
    height: options.isDetailGrid ? '100%' : defaultGridHeight,
    width: '100%',
    columnWidth: options?.columnWidth || '8rem',
    pager: useDefaultPager,
    paging: useDefaultPaging,
    scrolling: options?.scrolling || useDefaultScrolling,
    stateStoring: (options?.saveStateId || options?.id) && useDefaultStateStoring(options?.saveStateId || options?.id),
    repaintChangesOnly: true,
    columnFixing: {
      enabled: true,
    },

    export: defaultExport?.exportOptions ?? { enabled: false },
    onExporting: defaultExport?.onExporting,

    // sorting & filtering
    // https://js.devexpress.com/Demos/WidgetsGallery/Demo/DataGrid/MultipleSorting/Vue/Light/
    sorting: useDefaultSorting,
    filterRow: useDefaultFilterRow,
    grouping: options.isDetailGrid ? { visible: false } : useDefaultGrouping,
    groupPanel: options.isDetailGrid ? { visible: false } : useDefaultGroupPanel,

    // focused row
    // https://js.devexpress.com/Demos/WidgetsGallery/Demo/DataGrid/FocusedRow/Vue/Light/
    focusedRowEnabled: true,
    autoNavigateToFocusedRow: true,

    // show validation errors
    // https://supportcenter.devexpress.com/ticket/details/t933572/datagrid-how-to-display-validation-errors-that-came-from-the-server
    errorRowEnabled: false,
    onDataErrorOccurred: useDefaultErrorHandler,
    onInitialized: (e) => useDefaultOnInitialized(e, { ...currentOptions, appContext }),
  }, ...options}

  return currentOptions
}

export const useLocalCrudGridOptions = ({ load, remove = null, onAdd = null, onEdit = null, buttons = null, columns, ...options }) => {
  const rowButtons = buttons ?? [{ name: 'edit', onClick: (e) => onEdit(e.row.data) }, ...(remove ? [{ name: 'delete' }] : [])]
  return useDefaultGridOptions({
    dataSource: new CustomStore({ key: 'id', loadMode: 'raw', cacheRawData: false, load, remove }),
    columns: [
      {
        type: 'buttons',
        showInColumnChooser: false,
        buttons: rowButtons,
        fixed: false,
        visibleIndex: -1,
        width: `${rowButtons.length * 3}rem`,
      },
      ...columns,
    ],
    isDetailGrid: true,
    height: 'auto',
    remoteOperations: false,
    paging: { enabled: false },
    editing: useDefaultEditing(null, null, { allowAdding: false, allowUpdating: !!onEdit, allowDeleting: !!remove }),
    addButton: onAdd ? { onClick: onAdd } : undefined,
    onRowDblClick: (e) => onEdit?.(e.data),
    ...options,
  })
}

export const useODataStore = (url, options = null) => {
  const i18n = getI18n()
  const store = new ODataStore({
    url: url,
    beforeSend: (e) => {
      const webSiteStore = useWebSiteStore()

      if (options?.customParamsFn) {
        const params = options.customParamsFn(e.params)
        if (params) {
          e.params = {...e.params, ...params}
        }
      }
      e.headers = {
        ...e.headers,
        'Authorization': 'Bearer ' + webSiteStore.accessToken,
        'Accept-Language': i18n.locale.value,
        'x-odata': 'true',
      }

      // remove from params the id if the value is not a number
      if (e.params?.$filter && e.params.$filter.includes('id')) {
        const match = e.params.$filter.match(/id eq (\w+)/)
        if (!match || isNaN(match[1])) {
          e.params.$filter = e.params.$filter.replace(/id eq \'(.*)\'/, '')
        }
        if (e.params.$filter === '') {
          delete e.params.$filter
        }
      }

      if (e.method === 'PUT' || e.method === 'PATCH' || e.method === 'DELETE') {
        const id = e.url.match(/\((\d+)\)/)[1]
        e.url = url + id + '/'
      } else {
        e.url = url
      }
    },
    deserializeDates: false,
    withCredentials: true,
    key: options?.key || 'id',
    keyType: 'Int32',
    version: 4,
    ...options,
  })

  store.byKey = (key) => {
    // if (dataSource.cache.value && dataSource.cache.value.length > 0) {
    //   const element = dataSource.cache.value.find(x => x.id === key)
    //   if (element) {
    //     return new Deferred().resolve(element)
    //   }
    // }
    if (!key || key === '') {
      return store.load()
    }
    if (typeof key === 'string') {
      const searchExpr = options?.searchExpr || ['code']
      const filter = searchExpr.map((expr) => {
        return [expr, 'contains', key]
      })
      return store.load({ filter: filter })
    }
    return store.load({ filter: ['id', '=', key] })
  }

  return store
}

export const useODataSourceOptions = (url, options = null) => {
  const store = useODataStore(url, options)
  const dataSourceOptions = {
    store: store,
    pageSize: 20,
  }

  return { ...dataSourceOptions, ...options }
}

export const useODataSource = (url, options = null) => new DataSource(useODataSourceOptions(url, options))

export const useMask = () => {
  return {
    mobileMask: '000 00 00 0099',
    dateDisplayFormat: 'dd/MM/yyyy',
    dateSerializationFormat: 'yyyy-MM-dd',
    percentageMask: '#0.##%',
  }
}

export const useUpdateMasterDetail = (options = null) => {
  const repaintRow = async(gridRef, apiMethod, id, rowKey) => {
    if (id) {
      let data = null
      await apiMethod(id)
      .then(res => {
        const index = gridRef.value.instance.getRowIndexByKey(rowKey)
        const row = gridRef.value.instance.getVisibleRows()[index].data
        Object.assign(row, res.data)
        data = res.data
        gridRef.value.instance.repaintRows([index])
      })
      return data
    }
  }

  return { repaintRow }
}

export const useCustomValidation = (options = null) => {
  const webSiteStore = useWebSiteStore()

  const vatNumberValidation = (event, vatCountryId, isoCode, maxLength) => {
    return webSiteStore.countries.find(x => x.id === vatCountryId)?.iso3_code === isoCode ? event.value.length <= maxLength : true
  }

  return { vatNumberValidation }
}

export const useDefaultDialogTitle = () => {
  const { t } = getI18n()

  const defaultDialogTitle = (item, dialogName) => {
    const title = `${defaultActionTitle(item)} ${dialogName.toLocaleLowerCase()}`
    if (!item?.id) {
      return title
    }
    const subTitle = defaultItemTitle(item)
    return subTitle ? `${title} - ${subTitle}` : title
  }

  const defaultActionTitle = (item) => {
    return !item?.id ? t('common.add') : t('common.edit')
  }

  const defaultItemTitle = (item) => {
    let title = ''
    if (!item) {
      return title
    }
    if (item.name) {
      title += ` ${item.name}`
    }
    return title
  }

  return { defaultDialogTitle, defaultActionTitle, defaultItemTitle }
}
